// Multi-phase rolloff projects.
//
// A Project is the shared envelope for a large rolloff job (e.g. a month-long
// site) that needs a bin swapped out repeatedly. Each swap is its own child
// order (a "phase"); the project holds what's common across phases (customer,
// service location, municipality, government fields).
//
// Spec: docs/reference/implementation/multi-phase-rolloff-projects.md.
//
// The backend (/projects in app.py) already returns these camelCase shapes, so
// the fetch functions below pass them through. Phase promotion / invoicing /
// payment reuse the existing per-order endpoints (a phase is an order).

import { apiFetch } from '../lib/apiFetch';

/** Derived project status, computed from the child phases on read (no stored
 *  column): draft = no phase started; active = at least one phase started/in
 *  progress; completed = all phases stopped + paid; canceled = all canceled. */
export type ProjectStatus = 'draft' | 'active' | 'completed' | 'canceled';

/** One phase of a project — a single bin delivery + pickup, backed by its own
 *  order. A summary view of the underlying order, enough to render a phase card
 *  on the project work surface. */
export interface ProjectPhase {
  /** The underlying order id. */
  id: string;
  /** Booqable order id once the phase is promoted; null while still a local
   *  draft (progressive billing — future phases stay drafts until due). */
  booqable_id?: string | null;
  orderNumber?: string | null;
  /** 1-based position in the swap sequence (1 = initial drop). */
  phaseNumber: number;
  /** Human label: "Initial drop" for phase 1, then "Swap 1", "Swap 2", … for the
   *  bin swaps that follow (swaps numbered from 1, DRS convention). */
  phaseLabel?: string | null;
  /** Customer-facing id for this phase: `${projectNumber}.${phaseNumber}` (the
   *  app prefixes "P-"). */
  phaseDisplayId?: string;
  /** Delivery (drop) date — the order's starts_at. */
  startDate: string | null;
  /** Pickup date — the order's stops_at. The next phase's delivery defaults to
   *  this date (the swap hinge). */
  endDate: string | null;
  booqableStatus?: 'new' | 'draft' | 'reserved' | 'started' | 'stopped' | 'archived' | 'canceled';
  /** Null until promoted to Booqable (no invoice exists yet). */
  paymentStatus?: 'paid' | 'partially_paid' | 'payment_due' | 'overpaid' | 'process_deposit' | null;
  price: number;
  paidAmount: number;
  itemCount: number;
  /** The bin's product line title, mirrored locally so the phase card renders it
   *  without a live Booqable call. Null until mirrored (card falls back to an
   *  item count). */
  binLabel?: string | null;
  binQuantity?: number | null;
  /** The bin line's Booqable id + per-unit price (cents), mirrored so the inline
   *  price-override editor on the card seeds instantly without a live /bin-line
   *  fetch. Null for bins whose mirror predates the capture — the editor falls
   *  back to fetchPhaseBinLine in that case. */
  binLineId?: string | null;
  binUnitPriceInCents?: number | null;
  /** True once the Heavy material add-on line is on the phase's order. Mirrored
   *  server-side (see _mark_phase_heavy) so the card's Heavy checkbox reflects it
   *  without a live Booqable call. */
  hasHeavy?: boolean;
}

// Label for the project-closing pickup phase. Mirrors FINAL_PICKUP_LABEL in the
// backend's add_project_phase: it both names the phase and marks the project
// terminal (no phases can follow it). A final pickup is pickup-only — it hauls
// the last bin away, so it carries no delivery date, just a planned pickup.
// `isFinalPickup` keys card/strip/drawer behaviour off this label rather than a
// stored flag (phases carry no dedicated column).
export const FINAL_PICKUP_LABEL = 'Final pickup';
export const isFinalPickup = (p: { phaseLabel?: string | null }): boolean =>
  (p.phaseLabel || '') === FINAL_PICKUP_LABEL;

/** A multi-phase rolloff project: the parent envelope plus its ordered phases. */
export interface Project {
  id: string;
  /** Human-facing number; renders as `P-${projectNumber}`. */
  projectNumber: number;
  name?: string | null;
  customerId: string | null;
  customerName?: string | null;
  /** Derived from the phases (see ProjectStatus). */
  status: ProjectStatus;

  // Shared service location for every phase.
  deliveryAddress?: string | null;
  deliveryLatitude?: number | null;
  deliveryLongitude?: number | null;
  deliveryLocationName?: string | null;
  municipalityId?: string | null;

  // Shared government / procurement fields, copied onto each phase's invoice.
  contractNumber?: string | null;
  poNumber?: string | null;
  solicitationNumber?: string | null;

  /** Project-owned billing address override (display string), independent of the
   *  customer record. NULL = no override; the UI offers the customer's address
   *  when adding. */
  billingAddress?: string | null;

  /** Single prohibited-items acknowledgment for the whole project, cascaded onto
   *  each phase so they can reserve. NULL = not yet confirmed. */
  prohibitedItemsAcknowledged?: boolean | null;

  notes?: string | null;
  createdByName?: string | null;
  createdAt?: string;

  /** Phases in sequence order. */
  phases: ProjectPhase[];
}

// --- API client ---------------------------------------------------------------
// Request bodies use the snake_case keys the backend reads.

export interface ProjectInput {
  customer_id?: string;
  name?: string | null;
  delivery_address?: string | null;
  delivery_latitude?: number | null;
  delivery_longitude?: number | null;
  delivery_location_name?: string | null;
  municipality_id?: string | null;
  contract_number?: string | null;
  po_number?: string | null;
  solicitation_number?: string | null;
  billing_address?: string | null;
  prohibited_items_acknowledged?: boolean | null;
  notes?: string | null;
  archived?: boolean;
}

/** New phase: dates as YYYY-MM-DD. Omit pickup_date to default the delivery to
 *  the previous phase's pickup date (the swap hinge). */
export interface PhaseInput {
  pickup_date?: string;
  return_date?: string;
  phase_label?: string;
  /** Create the project-closing pickup phase ("Final pickup"): a single-day,
   *  pickup-only event with no new bin. The backend rejects it as the first
   *  phase, and rejects any phase added after it (it's terminal). */
  final_pickup?: boolean;
}

export const fetchProjects = async (): Promise<Project[]> => {
  const response = await apiFetch('/projects');
  if (!response.ok) throw new Error(`Failed to fetch projects: ${response.statusText}`);
  const data = await response.json();
  return (data.data || []) as Project[];
};

export const fetchProject = async (projectId: string): Promise<Project> => {
  const response = await apiFetch(`/projects/${projectId}`);
  if (!response.ok) throw new Error(`Failed to fetch project: ${response.statusText}`);
  const data = await response.json();
  return data.data as Project;
};

export const createProject = async (
  input: ProjectInput,
): Promise<{ project_id: string; project_number: number }> => {
  const response = await apiFetch('/projects', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw new Error(`Failed to create project: ${response.statusText}`);
  return response.json();
};

export const updateProject = async (projectId: string, input: ProjectInput): Promise<void> => {
  const response = await apiFetch(`/projects/${projectId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    // Surface the backend's reason (e.g. the 409 refusing to archive a project
    // with still-active phases) instead of a generic status line.
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Failed to update project: ${response.statusText}`);
  }
};

/** Append a phase (local-draft child order) to a project. Promote it later via
 *  the existing /orders/<id>/promote endpoint. */
export const addProjectPhase = async (
  projectId: string,
  input: PhaseInput = {},
): Promise<{ order_id: string; phase_number: number }> => {
  const response = await apiFetch(`/projects/${projectId}/phases`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw new Error(`Failed to add phase: ${response.statusText}`);
  return response.json();
};

// Serialize phase line-adds across every phase card on a project. Each add
// fans out into a promote → add-line → reconcile sequence of Booqable writes;
// staff picking rolloffs for several phases in quick succession would otherwise
// fire those sequences concurrently and trip Booqable's API rate limit (#431).
// The backend retries 429s, but serializing here keeps the bursts from forming
// in the first place — adds run one phase at a time. A module-level promise
// chain is sufficient: the project work surface is a single page, and a phase
// card can't enqueue twice (it shows the optimistic row, not the picker, while
// its add is in flight). Failures don't break the chain.
let phaseLineAddChain: Promise<unknown> = Promise.resolve();

/** Run `task` after any in-flight phase line-add completes, so concurrent
 *  rolloff picks across phases don't fan out parallel Booqable writes. Returns
 *  the task's own promise (resolves/rejects with its result); the shared chain
 *  swallows outcomes so one failure can't stall later adds. */
export const enqueuePhaseLineAdd = <T>(task: () => Promise<T>): Promise<T> => {
  const run = phaseLineAddChain.then(task, task);
  phaseLineAddChain = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
};

/** Promote a phase's draft order to Booqable so line items can be added (the
 *  sole add-line path requires a live Booqable order). Idempotent — a phase that
 *  already has a booqable_id returns it unchanged. The phase inherits the
 *  project's customer + service location, which is all promotion needs. */
export const promotePhase = async (phaseId: string): Promise<void> => {
  const response = await apiFetch(`/orders/${phaseId}/promote`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Failed to promote phase: ${response.statusText}`);
  }
};

/** Reserve a phase's order (draft → reserved), committing its inventory so the
 *  phase stops sitting in Booqable's draft limbo. Reuses the standard order
 *  reserve endpoint, which walks new→draft→reserved and enforces the
 *  prohibited-items gate (a phase inherits the project's single acknowledgment).
 *  Idempotent — a phase already reserved no-ops server-side. Throws on failure
 *  (e.g. no inventory for the window, or an unacknowledged project). */
export const reservePhase = async (phaseId: string): Promise<void> => {
  const res = await apiFetch(`/booqable/orders/${encodeURIComponent(phaseId)}/reserve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Failed to reserve phase: ${res.statusText}`);
  }
};

/** Resolve the tier-derived price (in cents) for a product on a phase's order
 *  WITHOUT creating a line. Lets the picker show the price the instant a rolloff
 *  is picked: the draft order row already carries the service municipality, so
 *  this resolves even before the phase is promoted, and it's a DB-only lookup
 *  (no Booqable round-trip). The authoritative price is re-resolved server-side
 *  on the actual add — this is the same value, shown ~instantly. Returns null
 *  when it can't be priced (unresolved address, Tier D, or any error). */
export const resolvePhaseLinePrice = async (
  phaseId: string,
  productId: string,
): Promise<number | null> => {
  try {
    const res = await apiFetch(
      `/pricing/resolve?order_id=${encodeURIComponent(phaseId)}` +
        `&product_id=${encodeURIComponent(productId)}`,
    );
    if (!res.ok) return null;
    const data = await res.json();
    return typeof data.price_in_cents === 'number' ? data.price_in_cents : null;
  } catch {
    return null;
  }
};

/** A phase's bin (product) line, fetched live for the inline price-override
 *  editor on the phase card. The phase mirror stores only the bin title, so the
 *  line id + current unit price come straight from Booqable on edit-open. */
export interface PhaseBinLine {
  lineId: string;
  priceEachInCents: number;
  quantity: number;
  title: string | null;
}

/** Fetch a phase's bin line (id + current per-unit price). Called when staff
 *  open the price-override editor on a phase card. Throws if the order has no
 *  product line (binless / not promoted) or on any error. */
export const fetchPhaseBinLine = async (phaseId: string): Promise<PhaseBinLine> => {
  const res = await apiFetch(`/booqable/orders/${encodeURIComponent(phaseId)}/bin-line`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || 'Could not load the bin line.');
  }
  return res.json();
};

/** Override the per-unit price (cents) on a phase's bin line. Reuses the order
 *  line price endpoint (set_line_price + mirror-totals refresh), so the phase
 *  card's order total reflects it on the next refetch. */
export const updatePhaseLinePrice = async (
  phaseId: string,
  lineId: string,
  priceInCents: number,
): Promise<void> => {
  const res = await apiFetch(
    `/booqable/orders/${encodeURIComponent(phaseId)}/lines/${encodeURIComponent(lineId)}/price`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ price_each_in_cents: priceInCents }),
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || 'Could not update the price.');
  }
};

/** Post-line-add bookkeeping for a phase (auto-specify units, re-sync the Extra
 *  Day line, ensure a pro forma, and refresh the mirror money totals the phase
 *  card reads). The add-line POST defers this off its critical path, so the card
 *  shows stale 0 items / $0 until it runs. Best-effort — never throws. */
export const reconcilePhaseAfterLine = async (
  phaseId: string,
  binLabel?: string,
): Promise<void> => {
  try {
    await apiFetch(`/booqable/orders/${phaseId}/reconcile-after-line`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // The bin label (from the Booqable-sourced product dropdown) is persisted
      // to the local mirror here so the phase card needs no live call on render.
      body: JSON.stringify(binLabel ? { bin_label: binLabel } : {}),
    });
  } catch (e) {
    console.error('phase post-line reconcile failed:', e);
  }
};

/** Apply the Heavy material line to a phase's order. The phaseId IS the underlying
 *  order id; the backend resolves the Booqable order, locates the non-trackable
 *  "Heavy" product, and books it at qty=1 (default price) — the same lookup the
 *  booking flow and OrderDetails use. Only 10cy/20cy roll-offs are eligible (the
 *  rolloff_sizes catalog); the caller gates the affordance. Throws on failure. */
export const addPhaseHeavyLine = async (phaseId: string): Promise<void> => {
  const res = await apiFetch(`/orders/${encodeURIComponent(phaseId)}/lines/heavy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Failed to add heavy material: ${res.statusText}`);
  }
};

/** Remove the Heavy material line from a phase's order. Mirrors addPhaseHeavyLine
 *  (the phaseId IS the order id); the backend finds the heavy line by its
 *  variation id and deletes it, leaving the tonnage-overage charge untouched.
 *  Throws on failure. */
export const removePhaseHeavyLine = async (phaseId: string): Promise<void> => {
  const res = await apiFetch(`/orders/${encodeURIComponent(phaseId)}/lines/heavy`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Failed to remove heavy material: ${res.statusText}`);
  }
};

/** Delete (archive) a phase. Guarded server-side to the LAST phase of a project
 *  and only when it's an un-promoted draft; promoted phases return 422. */
export const deletePhase = async (phaseId: string): Promise<void> => {
  const response = await apiFetch(`/projects/phases/${phaseId}`, { method: 'DELETE' });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Failed to delete phase: ${response.statusText}`);
  }
};

/** Remove the bin from a phase (clears all line items on its order, then
 *  refreshes totals). The card enforces one bin per phase, so this frees the
 *  inline picker again. No-op for an un-promoted phase. */
export const clearPhaseLines = async (phaseId: string): Promise<void> => {
  const response = await apiFetch(`/projects/phases/${phaseId}/lines`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Failed to remove bin: ${response.statusText}`);
  }
};

/** One phase's resolved window for the batch schedule save. ISO datetimes
 *  (midnight UTC) or null. */
export interface PhaseScheduleItem {
  order_id: string;
  starts_at: string | null;
  stops_at: string | null;
}

/** Persist the whole phase schedule in one transaction. The caller resolves the
 *  cascade locally (lib/phaseDates.ts) and sends the full sequence; the backend
 *  validates draft-only + membership and writes every phase atomically. This is
 *  the no-lag path — one round-trip for the entire chain — and returns the
 *  updated project so the caller can reseed from it. */
export const savePhaseSchedule = async (
  projectId: string,
  phases: PhaseScheduleItem[],
): Promise<Project> => {
  const response = await apiFetch(`/projects/${projectId}/phases/dates`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phases }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Failed to save phase schedule: ${response.statusText}`);
  }
  return (await response.json()).data as Project;
};

/** Edit a not-yet-promoted phase's delivery/pickup dates (YYYY-MM-DD). Writes
 *  through the existing Supabase order endpoint (PUT /orders/<id>); a promoted
 *  phase's dates must be changed in the order editor, where the Booqable sync +
 *  paid-order confirmation live. Pass null to clear a date. */
export const updatePhaseDates = async (
  orderId: string,
  dates: { startDate?: string | null; endDate?: string | null },
): Promise<void> => {
  const toIso = (d?: string | null) => (d ? `${d}T00:00:00.000Z` : null);
  const body: Record<string, string | null> = {};
  if ('startDate' in dates) body.starts_at = toIso(dates.startDate);
  if ('endDate' in dates) body.stops_at = toIso(dates.endDate);
  const response = await apiFetch(`/orders/${orderId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`Failed to update phase dates: ${response.statusText}`);
};
